import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import QueuePositionWidget from "../components/QueuePositionWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);
  const [orderId, setOrderId] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus("pending");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        if (response.data.order_id) setOrderId(response.data.order_id);
      } else if (response.data.status === "expired") {
        setStatus("failed");
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Payment status error:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center max-w-md">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-[#E07A5F] mx-auto mb-6 animate-spin" />
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
              Processing Payment...
            </h1>
            <p className="text-[#F4F1DE]/60">
              Please wait while we confirm your payment
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
              Payment Successful!
            </h1>
            <p className="text-[#F4F1DE]/60 mb-8">
              Thank you for your order. We'll start crafting your bespoke enclosure right away.
            </p>

            {orderId && <QueuePositionWidget orderId={orderId} />}

            <div className="space-y-4 mt-8">
              <Link to="/dashboard" data-testid="success-dashboard-link">
                <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                  View Order
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/configurator">
                <Button variant="outline" className="w-full border-white/20 text-[#F4F1DE]/80">
                  Build Another
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <CheckCircle className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
              Payment Processing
            </h1>
            <p className="text-[#F4F1DE]/60 mb-8">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            <Link to="/dashboard">
              <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">✕</span>
            </div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
              Payment Failed
            </h1>
            <p className="text-[#F4F1DE]/60 mb-8">
              Something went wrong with your payment. Please try again.
            </p>
            <Link to="/cart">
              <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                Return to Cart
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
